import { Box, SxProps, Typography, useTheme } from '@mui/material';
import { useDrawer } from 'services/drawer/hooks';
import { DrawerComponentType } from 'services/drawer/types';
import { Lock, LockOpen } from '@mui/icons-material';
import { CandidatesByCandidateBatchItem } from '../types/CandidatesByCandidateBatch';
import { useUpdateCandidateMutation } from 'generated-graphql/hooks/apollo-hooks';
import { useBottomDrawer } from 'core/stores/bottomDrawer/hooks';
import { ToastType, useToast } from 'services/toast';
import { Button } from 'system/atoms';
import { useState } from 'react';

const boxContainer: SxProps = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '20px'
};

const buttonSx: SxProps = {
  height: 56,
  padding: '0 24px 0 24px',
  display: 'flex',
  gap: '4px',
  flexShrink: 0
};

type Props = {
  candidateIdsSelected: string[];
  candidatesList: CandidatesByCandidateBatchItem[];
  onClearSelectedCandidates: () => void;
};

export const AnalyseActionsBottomDrawer: React.FC<Props> = ({
  candidateIdsSelected,
  candidatesList,
  onClearSelectedCandidates
}) => {
  const { palette } = useTheme();
  const { closeBottomDrawer } = useBottomDrawer();
  const { openDrawer } = useDrawer();
  const [updateCandidate] = useUpdateCandidateMutation();
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  if (!candidateIdsSelected.length) return null;

  // find the selected candidates
  const selectedCandidates = candidatesList.filter((candidate) =>
    candidateIdsSelected.includes(candidate.id)
  );

  const lockedCandidatesSelected = selectedCandidates.filter(
    (candidate) => candidate.isLocked
  );
  const unlockedCandidatesSelected = selectedCandidates.filter(
    (candidate) => !candidate.isLocked
  );

  const updateCandidatesIsLocked = async (
    candidatesToUpdate: CandidatesByCandidateBatchItem[]
  ) => {
    try {
      setIsLoading(true);

      await Promise.all(
        candidatesToUpdate.map(async (candidate) => {
          await updateCandidate({
            variables: {
              input: {
                id: candidate.id,
                isLocked: !candidate.isLocked
              }
            }
          });
        })
      );

      onClearSelectedCandidates?.();

      showToast({
        message: 'Applicants locked status updated successfully',
        type: ToastType.Success
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ ...boxContainer, borderColor: palette.customGrey.medium }}>
      <Typography
        variant="body2"
        width="140px"
        textAlign="right"
        flexShrink={0}
      >
        {`${candidateIdsSelected.length} applicant${
          candidateIdsSelected.length > 1 ? 's' : ''
        } selected`}
      </Typography>
      {unlockedCandidatesSelected.length > 0 && (
        <Button
          variant="primary"
          sx={buttonSx}
          onClick={() => {
            openDrawer({
              drawerContentType: DrawerComponentType.CandidateStatusDrawer,
              drawerComponentProps: {
                candidateIds: unlockedCandidatesSelected.map(
                  (candidate) => candidate.id
                ),
                onClearSelectedCandidates
              }
            });
          }}
        >
          Change status
        </Button>
      )}
      {unlockedCandidatesSelected.length > 0 && (
        <Button
          variant="secondary"
          sx={buttonSx}
          onClick={() => updateCandidatesIsLocked(unlockedCandidatesSelected)}
          loading={isLoading}
          disabled={isLoading}
        >
          <Lock color="primary" fontSize="small" />
          Lock status
        </Button>
      )}
      {lockedCandidatesSelected.length > 0 && (
        <Button
          variant="secondary"
          sx={buttonSx}
          onClick={() => updateCandidatesIsLocked(lockedCandidatesSelected)}
          loading={isLoading}
          disabled={isLoading}
        >
          <LockOpen color="primary" fontSize="small" />
          Unlock status
        </Button>
      )}
      <Button variant="tertiary" sx={buttonSx} onClick={closeBottomDrawer}>
        Cancel
      </Button>
    </Box>
  );
};
